
import _ from 'lodash';

import { CONNECTION_PROPERTIES_UPDATED } from '../connection';
import { ReducerRegistry, equals, set } from '../redux';

import {
    CONFIG_WILL_LOAD,
    DISABLE_AUTO_PIP,
    LOAD_CONFIG_ERROR,
    OVERWRITE_CONFIG,
    OVERWRITE_INTERFACE_CONFIG,
    PICTURE_IN_PICTURE_MODE,
    SET_CONFIG,
    UPDATE_CONFIG
} from './actionTypes';
import { _cleanupConfig } from './functions';

declare var interfaceConfig: Object;

/**
 * The initial state of the feature base/config when executing in a
 * non-React Native environment. The mandatory configuration to be passed to
 * MeetHourJS#init(). The app will download config.js from the Meet Hour
 * deployment and take its values into account but the values bellow will be
 * enforced (because they are essential to the correct execution of the
 * application).
 *
 * @type {Object}
 */
const INITIAL_NON_RN_STATE = {
    interfaceConfig: {},
    isPictureInPictureMode: false,
    disableAutoPiP: false,
    giphy: {
        // Whether the feature is enabled or not.
        enabled: true,

        // SDK API Key from Giphy.
        sdkKey: 'zMXVKoebcgwTRkSm1JjLe6dU5aLfxdRY',

        // Display mode can be one of:
        // - tile: show the GIF on the tile of the participant that sent it.
        // - chat: show the GIF as a message in chat
        // - all: all of the above. This is the default option
        displayMode: 'all',

        // How long the GIF should be displayed on the tile (in milliseconds).
        tileTime: 7000

        // Limit results by rating: g, pg, pg-13, r. Default value: g.
        // rating: 'pg',
        // The proxy server url for giphy requests in the web app.
        // proxyUrl: 'https://giphy-proxy.example.com',
    }
};

/**
 * The initial state of the feature base/config when executing in a React Native
 * environment. The mandatory configuration to be passed to MeetHourJS#init().
 * The app will download config.js from the Meet Hour deployment and take its
 * values into account but the values bellow will be enforced (because they are
 * essential to the correct execution of the application).
 *
 * @type {Object}
 */
const INITIAL_RN_STATE = {
    analytics: {},

    // Audio levels are needed for the native AudioLevelIndicator and dominant
    // speaker shadow.  The getStats()-based fallback in RTPStatsCollector
    // runs on its own interval — use 500ms on RN to limit bridge overhead
    // (default on web is 200ms via getSynchronizationSources which is cheaper).
    disableAudioLevels: false,
    audioLevelsInterval: 500,

    giphy: {
        // Whether the feature is enabled or not.
        enabled: true,

        // SDK API Key from Giphy.
        sdkKey: 'zMXVKoebcgwTRkSm1JjLe6dU5aLfxdRY',

        // Display mode can be one of:
        // - tile: show the GIF on the tile of the participant that sent it.
        // - chat: show the GIF as a message in chat
        // - all: all of the above. This is the default option
        displayMode: 'all',

        // How long the GIF should be displayed on the tile (in milliseconds).
        tileTime: 7000

        // Limit results by rating: g, pg, pg-13, r. Default value: g.
        // rating: 'pg',
        // The proxy server url for giphy requests in the web app.
        // proxyUrl: 'https://giphy-proxy.example.com',
    },

    remoteVideoMenu: {}
};

ReducerRegistry.register(
    'features/base/config',
    (state = _getInitialState(), action) => {
        switch (action.type) {
        case UPDATE_CONFIG:
            return _updateConfig(state, action);

        case CONFIG_WILL_LOAD:
            return {
                error: undefined,

                /**
                     * The URL of the location associated with/configured by this
                     * configuration.
                     *
                     * @type URL
                     */
                locationURL: action.locationURL
            };

        case CONNECTION_PROPERTIES_UPDATED: {
            const { region, shard } = action.properties;
            const { deploymentInfo } = state;

            if (deploymentInfo?.region === region && deploymentInfo?.shard === shard) {
                return state;
            }

            return {
                ...state,
                deploymentInfo: JSON.parse(JSON.stringify({
                    ...deploymentInfo,
                    region,
                    shard
                }))
            };
        }
        case LOAD_CONFIG_ERROR:
            // XXX LOAD_CONFIG_ERROR is one of the settlement execution paths of
            // the asynchronous "loadConfig procedure/process" started with
            // CONFIG_WILL_LOAD. Due to the asynchronous nature of it, whoever
            // is settling the process needs to provide proof that they have
            // started it and that the iteration of the process being completed
            // now is still of interest to the app.
            if (state.locationURL === action.locationURL) {
                return {
                    /**
                         * The {@link Error} which prevented the loading of the
                         * configuration of the associated {@code locationURL}.
                         *
                         * @type Error
                         */
                    error: action.error
                };
            }
            break;

        case SET_CONFIG:
            return _setConfig(state, action);

        case DISABLE_AUTO_PIP:
            return {
                ...state,
                disableAutoPiP: action.disableAutoPiP
            };

        case OVERWRITE_INTERFACE_CONFIG:
            return {
                ...state,
                interfaceConfig: action.interfaceconfig,
                ...Array.isArray(action.interfaceconfig?.TOOLBAR_BUTTONS)
                    ? { toolbarButtons: action.interfaceconfig.TOOLBAR_BUTTONS }
                    : {}
            };
        case PICTURE_IN_PICTURE_MODE:
            return {
                ...state,
                isPictureInPictureMode: action.isPictureInPictureMode
            };

        case OVERWRITE_CONFIG:
            return {
                ...state,
                ...action.config
            };
        }

        return state;
    }
);

/**
 * Gets the initial state of the feature base/config. The mandatory
 * configuration to be passed to MeetHourJS#init(). The app will download
 * config.js from the Meet Hour deployment and take its values into account but
 * the values bellow will be enforced (because they are essential to the correct
 * execution of the application).
 *
 * @returns {Object}
 */
function _getInitialState() {
    return navigator.product === 'ReactNative'
        ? INITIAL_RN_STATE
        : INITIAL_NON_RN_STATE;
}

/**
 * Reduces a specific Redux action SET_CONFIG of the feature
 * base/lib-meet-hour.
 *
 * @param {Object} state - The Redux state of the feature base/config.
 * @param {Action} action - The Redux action SET_CONFIG to reduce.
 * @private
 * @returns {Object} The new state after the reduction of the specified action.
 */
function _setConfig(state, { config }) {
    // The mobile app bundles meet-hour and lib-meet-hour at build time and
    // does not download them at runtime from the deployment on which it will
    // join a conference. The downloading is planned for implementation in the
    // future (later rather than sooner) but is not implemented yet at the time
    // of this writing and, consequently, we must provide legacy support in the
    // meantime.


    const translatedConfig = _translateLegacyConfig(config);

    const newState = _.merge(
        {},
        translatedConfig,
        { error: undefined },

        // The config of _getInitialState() is meant to override the config
        // downloaded from the Meet Hour deployment because the former contains
        // values that are mandatory.
        _getInitialState()
    );

    _cleanupConfig(newState);

    return equals(state, newState)
        ? state
        : { ...newState,
            interfaceConfig: state.interfaceConfig };
}

/**
 * Constructs a new config {@code Object}, if necessary, out of a specific
 * config {@code Object} which is in the latest format supported by meet-hour.
 * Such a translation from an old config format to a new/the latest config
 * format is necessary because the mobile app bundles meet-hour and
 * lib-meet-hour at build time and does not download them at runtime from the
 * deployment on which it will join a conference.
 *
 * @param {Object} oldValue - The config {@code Object} which may or may not be
 * in the latest form supported by meet-hour and from which a new config
 * {@code Object} is to be constructed if necessary.
 * @returns {Object} A config {@code Object} which is in the latest format
 * supported by meet-hour.
 */
function _translateLegacyConfig(oldValue: Object) {
    let newValue = oldValue;

    const oldConfigToNewConfig = {
        analytics: [
            [ 'analyticsScriptUrls', 'scriptURLs' ],
            [ 'googleAnalyticsTrackingId', 'googleAnalyticsTrackingId' ]
        ]
    };

    // Translate the old config properties into the new config properties.
    Object.keys(oldConfigToNewConfig).forEach(section => {
        if (typeof oldValue[section] !== 'object') {
            newValue = set(newValue, section, {});
        }

        for (const [ oldKey, newKey ] of oldConfigToNewConfig[section]) {
            if (oldKey in newValue && !(newKey in newValue[section])) {
                const v = newValue[oldKey];

                // Do not modify oldValue.
                if (newValue === oldValue) {
                    newValue = {
                        ...newValue
                    };
                }
                delete newValue[oldKey];

                // Do not modify the section because it may be from oldValue
                // i.e. do not modify oldValue.
                newValue[section] = {
                    ...newValue[section],
                    [newKey]: v
                };
            }
        }
    });

    if (
        (!Array.isArray(newValue.toolbarButtons) || newValue.toolbarButtons.length === 0)
        && typeof interfaceConfig === 'object'
        && Array.isArray(interfaceConfig.TOOLBAR_BUTTONS)
    ) {
        newValue.toolbarButtons = interfaceConfig.TOOLBAR_BUTTONS;
    }

    return newValue;
}

/**
 * Updates the stored configuration with the given extra options.
 *
 * @param {Object} state - The Redux state of the feature base/config.
 * @param {Action} action - The Redux action to reduce.
 * @private
 * @returns {Object} The new state after the reduction of the specified action.
 */
function _updateConfig(state, { config }) {
    const newState = _.merge({}, state, config);

    _cleanupConfig(newState);

    return equals(state, newState) ? state : newState;
}
